
import { Component, Prop, Watch, Vue, Emit } from "vue-property-decorator";
import { ActionPlanTask } from "@/store/tasks/types";
import vSelect from "vue-select";
import { validationMixin } from "vuelidate";
import { required, integer, minValue } from "vuelidate/lib/validators";
import { get } from "lodash";
import api from "@/services/api.service";

@Component({
	components: {
		"vue-select": vSelect,
	},
	mixins: [validationMixin],
	validations: {
		notificationData: {
			serverId: {
				required,
				integer,
				minValue: minValue(1),
			},
			templateId: {
				required,
				integer,
				minValue: minValue(1),
			},
			title: {
				required,
			},
		},
	},
})
export default class NotificationSetup extends Vue {
	@Prop()
	public value!: ActionPlanTask;

	public showErrorAnimation: boolean = false;
	public isNameEmpty: boolean = false;
	public notificationData: any = {
		title: "",
		serverId: null,
		templateId: null,
	};
	private integrationTemplates: any = [];
	private integrationTemplateServers: any = [];

	/**
	 * Lifecycle hook that is called when the component is mounted.
	 * Initializes the component and triggers initial validation.
	 */
	public async mounted(): Promise<void> {
		this.$v.$reset();

		const { data } = await api.fetchNotificationServers();
		this.integrationTemplateServers = data;
		this.loadFromTask();

		// Trigger initial validation
		this.onValidationChange(this.$v.$invalid);
	}

	/**
	 * Watcher for notificationData changes.
	 * Triggers validation and emits an input event if there are no validation errors.
	 * @param newValue - The new value of notificationData.
	 */
	@Watch("notificationData", { deep: true, immediate: true })
	private async onNotificationDataChange(newValue: any): Promise<void> {
		this.$v.$touch();

		if (!this.$v.$error) {
			this.onInput();
		}
	}

	/**
	 * Emits an input event containing the updated task data.
	 * @returns The updated task data.
	 */
	@Emit("input")
	private onInput(): any {
		return {
			...this.value,
			taskText: this.notificationData.title,
			taskData: {
				...this.notificationData,
			},
		};
	}

	/**
	 * Watcher for validation state changes.
	 * Emits a validate-task event with the current validation state.
	 * @param $invalid - The current validation state.
	 */
	@Watch("$v.$invalid")
	private onValidationChange($invalid: boolean): void {
		this.$emit("validate-task", !$invalid);
	}

	/**
	 * Loads notification templates for the currently selected server.
	 */
	private async loadTemplatesForServer(): Promise<void> {
		if (this.notificationData.serverId > 0) {
			const templates = await api.fetchIntegrationTemplates(this.notificationData.serverId);
			this.integrationTemplates = templates;
		}
	}

	/**
	 * Loads the notificationData from the taskData.
	 */
	private loadFromTask(): void {
		if (get(this, ["value", "taskData"], false)) {
			this.notificationData = {
				...this.value.taskData,
			};
		}

		this.loadTemplatesForServer();
	}

	/**
	 * Checks if the given template ID is valid.
	 * @param templateId - The ID of the template to check.
	 * @returns True if the template ID is valid, false otherwise.
	 */
	private CheckTemplateIsValid(templateId: number): boolean {
		const template = this.integrationTemplates.filter((f) => f.integrationTemplateId == templateId);
		return template ? template.length > 0 : false;
	}
}
